import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import Hero from '../components/Hero';
import ProductShowcase from '../components/ProductShowcase';
import InnovationSection from '../components/InnovationSection';
import CorporateSection from '../components/CorporateSection';
import ServicesGrid from '../components/ServicesGrid';
import { AnswerBlock, FaqList } from '../components/ui';
import Reveal from '../components/Reveal';
import { PRODUCTS, ADD_ONS } from '../data';
import { CITIES } from '../cities';
import { useSEO, BASE_URL, faqLd } from '../hooks/useSEO';
import { ROUTE_META } from '../seo/routes';
import { foto } from '../imagenes';

const FAQS = [
  { q: '¿Cuánto cuesta alquilar una cabina de fotos?', a: 'Depende del modelo, las horas, la ciudad y el número de invitados. No manejamos tarifa única porque un evento de 40 personas y uno de 300 no cuestan lo mismo. En la página de precios explicamos qué mueve el valor y te cotizamos el mismo día.' },
  { q: '¿Con cuánta anticipación debo reservar?', a: 'En temporada alta —noviembre, diciembre y los meses de matrimonios— los fines de semana se llenan con más de un mes. Fuera de temporada, dos o tres semanas suelen alcanzar.' },
  { q: '¿Qué necesitan del lugar?', a: 'Una toma de 110 v cerca, el espacio libre que indica cada ficha y acceso para ingresar los equipos. Si es al aire libre pedimos techo o carpa por si llueve.' },
  { q: '¿Cuántas horas se contratan normalmente?', a: 'Entre tres y cuatro horas, que es cuando se concentra el grueso de los invitados. Se pueden sumar horas si la fiesta se extiende.' },
];

const HomePage: React.FC = () => {
  useSEO({
    ...ROUTE_META['/'],
    keywords:
      'cabina de fotos, alquiler cabina de fotos, fotocabina, cabina de fotos bogota, cabina de fotos para bodas, espejo magico, cabina 360, photobooth colombia',
    canonicalPath: '/',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Cabinas de fotos en alquiler',
        numberOfItems: PRODUCTS.length,
        itemListElement: PRODUCTS.map((p, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: p.name,
          url: `${BASE_URL}/catalogo/${p.id}`,
          description: p.shortDescription,
        })),
      },
      faqLd(FAQS),
    ],
  });

  const showcase = PRODUCTS.filter((p) => p.family !== 'ESCENARIOS');
  const innovation = PRODUCTS.filter((p) => p.family === 'ESCENARIOS');

  return (
    <>
      <Hero />

      {/* ── Desde 2011 ── */}
      <section className="relative bg-cabina-cream bg-pattern py-28 md:py-32">
        <div
          className="absolute inset-0 bg-gradient-to-b from-cabina-cream via-white/80 to-cabina-cream"
          aria-hidden="true"
        />
        <div className="container relative z-10 mx-auto px-6">
          <div className="relative mx-auto max-w-3xl px-4 text-center">
            {/* Tiras de fotos flotando a los lados */}
            <div className="absolute -left-48 top-4 z-0 hidden md:block" aria-hidden="true">
              <div className="animate-float" style={{ animationDuration: '6s' }}>
                <img
                  {...foto('/images/tirilla1.jpg', '144px')}
                  alt=""
                  loading="lazy"
                  className="photo-strip w-28 rotate-[-12deg] hover:rotate-[-5deg] md:w-32 lg:w-36"
                />
              </div>
            </div>

            <div className="absolute -right-48 top-16 z-0 hidden md:block" aria-hidden="true">
              <div className="animate-float" style={{ animationDuration: '7s', animationDelay: '1s' }}>
                <img
                  {...foto('/images/tirilla2.jpg', '144px')}
                  alt=""
                  loading="lazy"
                  className="photo-strip w-28 rotate-[15deg] hover:rotate-[8deg] md:w-32 lg:w-36"
                />
              </div>
            </div>

            <Reveal>
              <span className="eyebrow-cdf relative z-10 mb-4">Desde 2011</span>
              <h2 className="relative z-10 mb-6 font-serif text-3xl italic leading-tight text-gray-900 md:text-6xl">
                Creando experiencias
                <br />
                <span className="font-bold not-italic">Memorables</span>
              </h2>
              <div className="rule-cdf relative z-10 mx-auto mb-8" />
              <p className="relative z-10 mx-auto max-w-2xl text-base leading-relaxed text-gray-600 md:text-lg">
                Lideramos el mercado de cabinas de fotos en Colombia. Garantizamos innovación
                continua, estética impecable y la tecnología más avanzada para bodas, eventos
                sociales y activaciones de marca.
              </p>

              {/* Cifras en clave editorial: datos que ya están publicados en
                  el sitio (año de inicio, modelos del catálogo, ciudades). */}
              <dl className="relative z-10 mx-auto mt-10 grid max-w-xl grid-cols-3 divide-x divide-cabina-red/20 border-y border-cabina-red/20 py-5 md:mt-12">
                {[
                  { label: 'Operando desde', value: '2011' },
                  { label: 'Modelos de cabina', value: '16' },
                  { label: 'Ciudades', value: String(CITIES.length) },
                ].map((d) => (
                  <div key={d.label} className="flex flex-col-reverse items-center px-2">
                    <dt className="mt-1.5 text-[9px] font-bold uppercase tracking-[0.2em] text-cabina-ink/50 md:text-[10px]">
                      {d.label}
                    </dt>
                    <dd className="font-serif text-3xl font-bold italic leading-none text-cabina-red md:text-5xl">
                      {d.value}
                    </dd>
                  </div>
                ))}
              </dl>

              {/* Los otros nombres con que la gente busca lo mismo (Search
                  Console, oct 2026: photobooth, stand de fotos, foto cabina). */}
              <p className="relative z-10 mx-auto mt-8 max-w-xl text-sm italic leading-relaxed text-gray-500">
                ¿Photobooth, stand de fotos, foto cabina o cabina fotográfica? Es lo mismo: una
                cabina donde tus invitados posan y se llevan su foto impresa al instante.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Respuesta corta: lo primero legible tras la portada */}
      <section className="bg-white py-16 md:py-20" aria-labelledby="respuesta-home">
        <div className="container mx-auto max-w-5xl px-6">
          <AnswerBlock
            id="respuesta-home"
            question="¿Qué es y qué incluye alquilar una cabina de fotos para un evento?"
            answer="Una cabina de fotos es una estación donde los invitados posan y reciben su foto impresa en el momento. El alquiler incluye transporte, montaje, un operador que acompaña durante todo el evento, impresión ilimitada mientras la cabina esté abierta, props, el marco personalizado con el nombre y la fecha, y una galería digital con todas las fotos. Trabajamos en Colombia desde 2011, con 16 modelos entre cabinas, espejos fotográficos, video 360 y escenarios."
          />
        </div>
      </section>

      <ProductShowcase products={showcase} />

      <InnovationSection products={innovation} />

      <CorporateSection />

      <ServicesGrid services={ADD_ONS} />

      {/* ── Cobertura ── */}
      <section className="bg-cabina-cream py-24" aria-labelledby="cobertura-home">
        <div className="container mx-auto px-6">
          <Reveal className="mb-14 text-center">
            <span className="eyebrow-cdf mb-4">Cobertura</span>
            <h2
              id="cobertura-home"
              className="mb-6 font-serif text-4xl font-bold text-black md:text-6xl"
            >
              Llegamos a <span className="italic text-cabina-red">tu ciudad</span>
            </h2>
            <div className="rule-cdf mx-auto mb-8" />
            <p className="mx-auto max-w-2xl text-lg text-gray-600">
              Trabajamos ciudad por ciudad, con la logística que cada una exige. Entra a la tuya
              y mira cómo operamos ahí.
            </p>
          </Reveal>

          <ul className="mx-auto grid max-w-5xl grid-cols-2 gap-4 md:grid-cols-4">
            {CITIES.map((city, i) => (
              <Reveal as="li" key={city.slug} delay={(i % 4) * 70}>
                <Link
                  to={`/ciudades/${city.slug}`}
                  className="group flex h-full items-center justify-between gap-3 rounded-xl border border-cabina-red/10 bg-white px-5 py-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-cabina-red/40 hover:shadow-lg"
                >
                  <span>
                    <span className="block font-serif text-lg font-bold text-cabina-dark">
                      {city.name}
                    </span>
                    <span className="mt-0.5 block text-[11px] uppercase tracking-widest text-gray-400">
                      {city.department}
                    </span>
                  </span>
                  <MapPin
                    size={16}
                    className="shrink-0 text-cabina-red transition-transform group-hover:scale-125"
                  />
                </Link>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Preguntas frecuentes ── */}
      <section className="bg-white py-24" aria-labelledby="faq-home">
        <div className="container mx-auto max-w-3xl px-6">
          <Reveal className="mb-12 text-center">
            <span className="eyebrow-cdf mb-4">Resolvemos</span>
            <h2 id="faq-home" className="font-serif text-4xl font-bold text-black md:text-5xl">
              Preguntas <span className="italic text-cabina-red">frecuentes</span>
            </h2>
            <div className="rule-cdf mx-auto mt-8" />
          </Reveal>

          <FaqList faqs={FAQS} />

          <div className="mt-12 flex flex-wrap justify-center gap-5">
            <Link to="/contacto" className="btn-cdf">
              Pedir cotización
              <ArrowRight size={16} />
            </Link>
            <Link to="/precios" className="btn-outline-cdf">
              Cómo cotizamos
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
